/*
 * Cross-tab sync for the operator session (flows/admin-auth). The token itself stays in
 * sessionStorage — deliberately tab-scoped, so it still dies with the last tab (see
 * admin-session-store) — and this module carries it between tabs over a BroadcastChannel instead of
 * widening where it is stored.
 *
 * It exists because sessionStorage is per-browsing-context: cmd-click, middle-click and "open link
 * in new tab" always create a noopener context, and a pasted URL has no opener at all, so the new
 * tab boots with an empty store and the guard bounces it to /login (CAE-576). Here the booting tab
 * asks the tabs that are already open for the token and writes the reply into its own store.
 */

const TOKEN_REQUEST = 'admin-session/token-request';
const TOKEN_OFFER = 'admin-session/token-offer';
const SIGN_OUT = 'admin-session/sign-out';

export interface AdminSessionSync {
  /** Ask the open tabs for a token. Resolves with the first offer, or null if none arrives. */
  requestToken(): Promise<string | null>;
  /** Tell the open tabs the session is over, so signing out here signs out everywhere. */
  broadcastSignOut(): void;
  /** Stop serving other tabs and release the channel. */
  close(): void;
}

export interface AdminSessionSyncOptions {
  /** What this tab offers when another asks — its own stored token, or null when signed out. */
  readonly readToken: () => string | null;
  /** Another tab dropped the session. */
  readonly onRemoteSignOut: () => void;
  /**
   * Defaults to the shared channel name. Tests override it so two cases cannot answer each other —
   * they still speak the real protocol over a real BroadcastChannel, just on a private name.
   */
  readonly channelName?: string;
  /** How long to wait for an offer before concluding no tab is open to answer. */
  readonly replyWindowMs?: number;
}

/*
 * The wait a booting tab gives the open tabs before deciding it is alone. Only ever paid on a boot
 * with an empty store, and the reply is a same-origin postMessage round trip (sub-millisecond in
 * practice), so this is a generous ceiling rather than a typical cost. It is also the delay a
 * genuinely signed-out operator waits before /login appears, which is why it is not larger.
 */
const DEFAULT_REPLY_WINDOW_MS = 250;

/**
 * Start participating in the cross-tab session protocol. Serving begins immediately — a tab must be
 * able to answer another tab's request from the moment it boots.
 */
export function createAdminSessionSync({
  readToken,
  onRemoteSignOut,
  channelName = 'cou.adminSession',
  replyWindowMs = DEFAULT_REPLY_WINDOW_MS,
}: AdminSessionSyncOptions): AdminSessionSync {
  const bus = new BroadcastChannel(channelName);

  const serve = (event: MessageEvent) => {
    const message = event.data as { type?: string } | null;
    if (message?.type === SIGN_OUT) {
      onRemoteSignOut();
      return;
    }
    if (message?.type !== TOKEN_REQUEST) {
      return;
    }
    // A signed-out tab stays silent rather than offering a null: the requester's own timeout is
    // what decides "nobody can help", so one signed-out tab cannot cut that short for the rest.
    const token = readToken();
    if (token !== null) {
      bus.postMessage({ type: TOKEN_OFFER, token });
    }
  };
  bus.addEventListener('message', serve);

  return {
    requestToken() {
      return new Promise<string | null>((resolve) => {
        const settle = (token: string | null) => {
          clearTimeout(timer);
          bus.removeEventListener('message', onOffer);
          resolve(token);
        };
        const onOffer = (event: MessageEvent) => {
          const message = event.data as { type?: string; token?: unknown } | null;
          if (message?.type === TOKEN_OFFER && typeof message.token === 'string') {
            settle(message.token);
          }
        };
        const timer = setTimeout(() => {
          settle(null);
        }, replyWindowMs);

        bus.addEventListener('message', onOffer);
        bus.postMessage({ type: TOKEN_REQUEST });
      });
    },
    broadcastSignOut() {
      bus.postMessage({ type: SIGN_OUT });
    },
    close() {
      bus.removeEventListener('message', serve);
      bus.close();
    },
  };
}
